// Wave VIII Frontend Integration · AIChatWidget.
// V12 Achromatic Strict · floating support chat (SupportEngine LIVE · Wave VIII.I).
// Universal mount in V12UserLayout → one global instance only.
// Endpoints:
//   GET  /api/v2/ai/support/sessions/me       → list recent sessions
//   POST /api/v2/ai/support/ask               → ask question (creates/reuses session)
//   POST /api/v2/ai/support/escalate/:sid     → open human ticket
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, X, Send, LifeBuoy, Loader2 } from "lucide-react";

import { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

async function apiPost(path, body) {
    const r = await fetch(`${API}${path}`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body || {}),
    });
    const text = await r.text();
    let data = null; try { data = text ? JSON.parse(text) : null; } catch { /* */ }
    if (!r.ok) {
        const e = new Error(`HTTP ${r.status}`);
        e.status = r.status;
        e.data = data;
        throw e;
    }
    return data;
}

async function apiGet(path) {
    const r = await fetch(`${API}${path}`, { credentials: "include" });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return r.json();
}

const INITIAL_SUGGESTIONS = [
    "Come compro crediti?",
    "Che differenza c'è tra i piani?",
    "Come funziona il marketplace?",
];

export default function AIChatWidget() {
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [sessionId, setSessionId] = useState(null);
    const [messages, setMessages] = useState([]);  // {role, content, metadata?}
    const [input, setInput] = useState("");
    const [sending, setSending] = useState(false);
    const [followups, setFollowups] = useState(INITIAL_SUGGESTIONS);
    const [escalatePending, setEscalatePending] = useState(false);
    const [rateLimited, setRateLimited] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");
    const scrollRef = useRef(null);

    // Only render for authenticated users
    const canRender = Boolean(user);

    // On first open · try to load most-recent active session's messages
    useEffect(() => {
        if (!open || !canRender || sessionId) return;
        (async () => {
            try {
                const d = await apiGet("/v2/ai/support/sessions/me?limit=5");
                const items = d?.items || [];
                const active = items.find((s) => s.status === "active");
                if (active) {
                    const full = await apiGet(
                        `/v2/ai/support/sessions/${encodeURIComponent(active.session_id)}`,
                    );
                    setSessionId(full.session_id);
                    setMessages((full.messages || []).map((m) => ({
                        role: m.role,
                        content: m.content,
                        metadata: m.metadata || {},
                    })));
                    const lastAi = [...(full.messages || [])].reverse()
                        .find((m) => m.role === "ai");
                    const fups = lastAi?.metadata?.followups || [];
                    setFollowups(fups.length ? fups : INITIAL_SUGGESTIONS);
                    setEscalatePending(active.status !== "escalated"
                        && Boolean(lastAi?.metadata?.escalate));
                }
            } catch (_e) { /* non-blocking */ }
        })();
    }, [open, canRender, sessionId]);

    // Scroll to bottom on new messages
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages, sending]);

    const send = useCallback(async (text) => {
        const q = (text || "").trim();
        if (!q || sending) return;
        setErrorMsg("");
        setRateLimited(false);
        setSending(true);
        // Optimistic append user message
        setMessages((m) => [...m, { role: "user", content: q }]);
        setInput("");
        try {
            const data = await apiPost("/v2/ai/support/ask", {
                question: q,
                session_id: sessionId || undefined,
            });
            setSessionId(data.session_id);
            setMessages((m) => [...m, {
                role: "ai",
                content: data.answer,
                metadata: {
                    citations: data.citations || [],
                    followups: data.suggested_followups || [],
                    escalate: Boolean(data.escalate),
                },
            }]);
            setFollowups((data.suggested_followups || []).slice(0, 3));
            setEscalatePending(Boolean(data.escalate));
        } catch (e) {
            if (e.status === 429) {
                setRateLimited(true);
                setErrorMsg("Troppi messaggi · riprova tra un minuto.");
            } else if (e.status === 401) {
                setErrorMsg("Sessione scaduta · ricarica la pagina.");
            } else {
                setErrorMsg("Impossibile inviare il messaggio · riprova.");
            }
        } finally {
            setSending(false);
        }
    }, [sessionId, sending]);

    const onSubmit = (e) => {
        e.preventDefault();
        send(input);
    };

    const onEscalate = useCallback(async () => {
        if (!sessionId) return;
        try {
            await apiPost(
                `/v2/ai/support/escalate/${encodeURIComponent(sessionId)}`,
                { subject: "Richiesta assistenza umana", priority: "normal" },
            );
            setMessages((m) => [...m, {
                role: "ai",
                content: "Ticket aperto · un operatore ti contatterà a breve. Grazie!",
                metadata: { escalation_confirmed: true },
            }]);
            setEscalatePending(false);
        } catch (_e) {
            setErrorMsg("Non è stato possibile aprire il ticket · riprova.");
        }
    }, [sessionId]);

    if (!canRender) return null;

    // Render in a portal to document.body so the fixed positioning and
    // z-index escape any ancestor stacking context (cookie banner, etc.)
    if (typeof document === "undefined") return null;

    return createPortal(
        <>
            {/* Floating button */}
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-label={open ? "Chiudi assistenza AI" : "Apri assistenza AI"}
                data-testid="ai-chat-widget-toggle"
                className="fixed bottom-5 right-5 z-[70] flex h-12 w-12 items-center justify-center rounded-full border border-neutral-700 bg-neutral-900 text-neutral-200 shadow-lg transition-colors hover:border-neutral-500 hover:text-neutral-50"
            >
                {open ? <X className="h-5 w-5" /> : <MessageCircle className="h-5 w-5" />}
                {escalatePending && !open && (
                    <span
                        className="absolute top-1 right-1 h-2 w-2 rounded-full bg-neutral-200"
                        aria-hidden="true"
                    />
                )}
            </button>

            {/* Drawer */}
            <AnimatePresence>
                {open && (
                    <motion.aside
                        key="ai-chat-drawer"
                        initial={{ x: 420, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        exit={{ x: 420, opacity: 0 }}
                        transition={{ type: "spring", damping: 24, stiffness: 220 }}
                        className="fixed inset-y-0 right-0 z-[75] flex w-full max-w-[400px] flex-col border-l border-neutral-800 bg-neutral-950 shadow-2xl sm:bottom-5 sm:right-5 sm:top-auto sm:h-[600px] sm:max-h-[85vh] sm:rounded-lg sm:border"
                        data-testid="ai-chat-widget-drawer"
                    >
                        {/* Header */}
                        <header className="flex items-center justify-between gap-2 border-b border-neutral-800 px-4 py-3">
                            <div className="flex items-center gap-2">
                                <MessageCircle className="h-4 w-4 text-neutral-400" />
                                <h3 className="text-sm font-medium text-neutral-100">
                                    AI Support
                                </h3>
                                <span className="text-[11px] text-neutral-500">
                                    · chiedi qualsiasi cosa
                                </span>
                            </div>
                            <button
                                type="button"
                                onClick={() => setOpen(false)}
                                aria-label="Chiudi"
                                data-testid="ai-chat-widget-close"
                                className="text-neutral-500 hover:text-neutral-100"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </header>

                        {/* Messages */}
                        <div
                            ref={scrollRef}
                            className="flex-1 space-y-3 overflow-y-auto px-4 py-4"
                            data-testid="ai-chat-widget-history"
                        >
                            {messages.length === 0 && (
                                <div className="rounded-md border border-neutral-800 bg-neutral-900 p-3 text-xs text-neutral-400">
                                    Ciao {user?.name?.split(" ")[0] || ""}! Sono l'assistente
                                    AI di GlobalLeadSuite. Fammi una domanda su crediti,
                                    piani, marketplace, AI engines o qualsiasi altra cosa.
                                </div>
                            )}
                            {messages.map((m, i) => (
                                <div
                                    key={i}
                                    data-testid={`ai-chat-msg-${m.role}-${i}`}
                                    className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                                >
                                    <div
                                        className={`max-w-[85%] whitespace-pre-wrap rounded-lg border px-3 py-2 text-xs ${
                                            m.role === "user"
                                                ? "border-neutral-700 bg-neutral-800 text-neutral-100"
                                                : "border-neutral-800 bg-neutral-900 text-neutral-200"
                                        }`}
                                    >
                                        {m.content}
                                        {m.role === "ai" && (m.metadata?.citations || []).length > 0 && (
                                            <div className="mt-2 border-t border-neutral-800 pt-1 text-[10px] text-neutral-500">
                                                Fonti: {(m.metadata.citations || []).join(" · ")}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                            {sending && (
                                <div className="flex items-center gap-2 text-xs text-neutral-500"
                                       data-testid="ai-chat-thinking">
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                    L'AI sta pensando…
                                </div>
                            )}
                            {errorMsg && (
                                <div
                                    data-testid="ai-chat-error"
                                    className="rounded-md border border-neutral-700 bg-neutral-900 px-3 py-2 text-xs text-neutral-300"
                                >
                                    {errorMsg}
                                </div>
                            )}
                        </div>

                        {/* Followups + escalate */}
                        {(followups.length > 0 || escalatePending) && (
                            <div className="border-t border-neutral-800 px-4 py-2">
                                {followups.length > 0 && !sending && (
                                    <div className="mb-2 flex flex-wrap gap-1.5">
                                        {followups.slice(0, 3).map((f, i) => (
                                            <button
                                                key={i}
                                                type="button"
                                                onClick={() => send(f)}
                                                data-testid={`ai-chat-followup-${i}`}
                                                className="rounded-full border border-neutral-800 bg-neutral-900 px-2.5 py-1 text-[11px] text-neutral-400 hover:border-neutral-600 hover:text-neutral-200"
                                            >
                                                {f}
                                            </button>
                                        ))}
                                    </div>
                                )}
                                {escalatePending && (
                                    <button
                                        type="button"
                                        onClick={onEscalate}
                                        data-testid="ai-chat-escalate"
                                        className="flex w-full items-center justify-center gap-1.5 rounded-md border border-neutral-700 bg-neutral-900 px-3 py-1.5 text-xs text-neutral-200 hover:border-neutral-500"
                                    >
                                        <LifeBuoy className="h-3.5 w-3.5" />
                                        Non ti ha aiutato? Parla con un umano
                                    </button>
                                )}
                            </div>
                        )}

                        {/* Input */}
                        <form
                            onSubmit={onSubmit}
                            className="flex items-end gap-2 border-t border-neutral-800 px-4 py-3"
                        >
                            <textarea
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && !e.shiftKey) {
                                        e.preventDefault();
                                        send(input);
                                    }
                                }}
                                rows={1}
                                placeholder={rateLimited
                                    ? "Troppi messaggi · attendi un minuto"
                                    : "Scrivi la tua domanda…"}
                                disabled={sending || rateLimited}
                                data-testid="ai-chat-widget-input"
                                className="min-h-[36px] max-h-24 flex-1 resize-none rounded-md border border-neutral-800 bg-neutral-900 px-2.5 py-1.5 text-xs text-neutral-100 placeholder-neutral-600 focus:border-neutral-500 focus:outline-none disabled:opacity-50"
                            />
                            <button
                                type="submit"
                                disabled={sending || rateLimited || !input.trim()}
                                data-testid="ai-chat-widget-send"
                                aria-label="Invia"
                                className="rounded-md border border-neutral-700 bg-neutral-900 p-2 text-neutral-300 hover:border-neutral-500 hover:text-neutral-100 disabled:opacity-40"
                            >
                                {sending
                                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                    : <Send className="h-3.5 w-3.5" />}
                            </button>
                        </form>
                    </motion.aside>
                )}
            </AnimatePresence>
        </>,
        document.body,
    );
}
